import { motion } from "framer-motion";
import { BookOpen, Landmark, Sparkles, Users2 } from "lucide-react";

const leaders = [
  "J.R.D. Tata",
  "Nani Palkhivala",
  "H.P. Nanda",
  "L.M. Thapar",
  "A.N. Haksar",
  "Raunaq Singh",
];

// const sectors = [
//   "Agriculture",
//   "Banking",
//   "Taxation",
//   "Hydropower",
//   "Education",
//   "Healthcare",
//   "Manufacturing",
//   "Finance",
// ];

export default function LegacySection() {
  return (
    <section id="legacy" className="relative overflow-hidden bg-[#FAF7F2] py-20 md:py-4">
      {/* Decorative */}
      <div className="pointer-events-none absolute right-0 top-0 h-72 w-72 translate-x-1/3 -translate-y-1/3 rounded-full bg-[#DA2128]/5" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center md:mb-16">
          {/* <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-3 inline-block text-xs font-bold tracking-[0.2em] text-[#DA2128] uppercase"
          >
            A Century of Excellence
          </motion.span> */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-serif text-4xl font-medium leading-tight tracking-tight sm:text-3xl lg:text-4xl"
          >
            Our {" "}
            <motion.span 
                className="italic text-[#DA2128]"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
            Legacy
            </motion.span>
          </motion.h2>

          <div className="mx-auto mt-4 accent-bar" />
        </div>

        <div className="grid items-start gap-10 lg:grid-cols-5 lg:gap-14">
          {/* Left content - 3 cols */}
          <div className="space-y-6 lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-4 text-sm leading-relaxed text-gray-600 sm:text-base"
            >
              <p>
                ASSOCHAM began its journey as India's Apex Chamber of Commerce
                in <strong className="text-gray-900">1920</strong>. Founded on
                the vision of nation-building, the Chamber has been an integral
                part of India's economic history and continues to play a pivotal
                role in shaping the country's growth.
              </p>
              <p>
                With a rich legacy of being led by stalwarts of independent India,
                ASSOCHAM has consistently demonstrated its ability to evolve with
                the changing needs of Corporate India. Today, the Chamber has
                emerged as the{" "}
                <strong className="text-gray-900">Knowledge Chamber</strong>,
                leveraging India's strengths across sectors to advance the
                nation's position in the knowledge-driven global economy.
              </p>
              <p>
                ASSOCHAM traces its roots to the{" "}
                <strong className="text-gray-900">Bengal Chamber</strong>, a
                prominent and influential voice representing trade interests in
                British India during the late 19th and early 20th centuries.
                Established in 1853, the Bengal Chamber, in turn, traces its
                origins to the Calcutta Chamber, believed to have been founded
                in 1833.
              </p>
              <p>
                Within ASSOCHAM's fold are not only leading industries and trade
                bodies but also MSMEs, sectoral associations, and professional
                organisations operating across diverse fields. This diverse
                membership enables ASSOCHAM to effectively represent the interests
                of businesses across the Indian economy.
              </p>
            </motion.div>

            {/* Sector tags */}
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap gap-2"
            >
              {sectors.map((s) => (
                <span
                  key={s}
                  className="rounded-full border border-[#DA2128]/20 bg-white px-3.5 py-1.5 text-xs font-semibold text-[#DA2128] transition-colors hover:bg-[#DA2128] hover:text-white"
                >
                  {s}
                </span>
              ))}
              <span className="rounded-full bg-[#DA2128] px-3.5 py-1.5 text-xs font-semibold text-white">
                + Emerging Industries
              </span>
            </motion.div> */}
          </div>

          {/* Right side cards - 2 cols */}
          <div className="flex flex-col gap-5 lg:col-span-2">
            {/* Historic image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="img-reveal overflow-hidden rounded-2xl shadow-xl"
            >
              <img
                src="https://images.pexels.com/photos/36773368/pexels-photo-36773368.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=500&w=700"
                alt="Historic Kolkata - Victoria Memorial"
                className="h-48 w-full object-cover sm:h-56"
              />
              <div className="bg-white p-4">
                <div className="flex items-center gap-2">
                  <Landmark size={16} className="text-[#DA2128]" />
                  <p className="text-sm font-bold text-gray-900">
                    Roots in Calcutta
                  </p>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  From the Calcutta Chamber (1833) to Bengal Chamber (1853) to
                  ASSOCHAM (1920)
                </p>
              </div>
            </motion.div>

            {/* Leaders card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="rounded-2xl border border-gray-100 bg-white p-6 shadow-lg"
            >
              <div className="mb-4 flex items-center gap-2">
                <Users2 size={18} className="text-[#DA2128]" />
                <h3 className="text-sm font-bold text-gray-900">
                  Led by Stalwarts
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {leaders.map((name, i) => (
                  <motion.div
                    key={name}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.05 }}
                    className="flex items-center gap-2 rounded-lg bg-[#FAF7F2] px-3 py-2"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#DA2128]/10 text-[10px] font-bold text-[#DA2128]">
                      {name.charAt(0)}
                    </span>
                    <span className="text-xs font-medium text-gray-700">
                      {name}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Knowledge chamber badge */}
           
          </div>
        </div>

        {/* Bottom heritage banner */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-14 grid overflow-hidden rounded-3xl bg-white shadow-xl sm:grid-cols-3"
        >
          {[
            {
              icon: BookOpen,
              year: "1833",
              label: "Calcutta Chamber Founded",
            },
            {
              icon: Landmark,
              year: "1853",
              label: "Bengal Chamber Established",
            },
            {
              icon: Sparkles,
              year: "1920",
              label: "ASSOCHAM Journey Begins",
            },
          ].map((item, i) => (
            <div
              key={item.year}
              className={`flex items-center gap-4 p-6 ${
                i < 2 ? "border-b border-gray-100 sm:border-b-0 sm:border-r" : ""
              }`}
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#DA2128]/10 text-[#DA2128]">
                <item.icon size={22} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#DA2128]">{item.year}</p>
                <p className="text-xs font-medium text-gray-500">{item.label}</p>
              </div>
            </div>
          ))}
        </motion.div> */}
      </div>
    </section>
  );
}
